import { Injectable } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import { signInWithCustomToken } from '@firebase/auth';
import { collection, collectionData, doc, docData, Firestore, query, where } from '@angular/fire/firestore';
import { getDownloadURL, ref, Storage } from '@angular/fire/storage';
import { uniqBy } from 'lodash';
import { Router } from '@angular/router';

import { Roles, UserWithId } from '@innersight-os/shared/firestore-interfaces';
import { combineLatestOrEmptyArray } from '@innersight-os/shared/util-rxjs';
import { Observable, of, concat, EMPTY, from, combineLatest, timer, ignoreElements, interval, catchError } from 'rxjs';
import { switchMap, map, mergeAll, groupBy, scan, mergeMap, shareReplay, throttleTime, retry, takeWhile } from 'rxjs/operators';
import { SquadsService } from '../squads/squads.service';
import { userNamesAndEmailsDropdownListSort } from '@innersight-os/shared/util'
import { DocumentData } from '@firebase/firestore-types';

type OnboardingStage = 'unauthenticated' | 'email-not-verified' | 'unauthorised' | 'onboarded'

export interface UserWithPhoto extends UserWithId {
  photoPath: string; // non-optional
  photoURL: string;
}

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  constructor(
    private auth: Auth,
    private db: Firestore,
    private storage: Storage,
    private squads: SquadsService,
    private router: Router,
  ) {}

  me$: Observable<UserWithId | null> = authState(this.auth).pipe(
    switchMap(authUser =>
      authUser === null
        ? of(null)
        : this.getUser(authUser.uid)
    ),
    shareReplay({bufferSize: 1, refCount: true}),
  )

  myRoles$: Observable<Roles | null> = this.me$.pipe(
    map(user => user?.roles)
  )

  iAmAnAdmin$: Observable<boolean> = this.myRoles$.pipe(
    map(roles => !!roles?.admin)
  )

  iCanAssign$ = this.myRoles$.pipe(
    map(roles => roles?.admin || roles?.manager)
  )

  iCanAttribute$ = this.iAmAnAdmin$

  iAmSegmenter$ = this.myRoles$.pipe(
    map(roles => !!roles?.segmenter),
  )

  iAmSegmenterOnly$ = this.myRoles$.pipe(
    map(roles => !roles?.admin && !roles?.manager && roles?.segmenter),
  )

  iAmSelfServe$ = this.myRoles$.pipe(
    map(roles => !!roles?.selfServe),
  )

  myOnboardingStage$: Observable<OnboardingStage> =
    authState(this.auth).pipe(
      switchMap(authUser => {

        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');

        if (authUser === null) {
          if (token) {
            return from(signInWithCustomToken(this.auth, token)).pipe(
              map(() => {
                this.removeTokenFromUrl();
                return 'onboarded' as OnboardingStage;
              }),
              catchError(error => {
                console.error('Error signing in with custom token:', error);
                return of('unauthenticated' as OnboardingStage);
              })
            );
          }
          return of<OnboardingStage>('unauthenticated');
        } else {
          if (token) {
            this.removeTokenFromUrl();
          }

          const statusAfterVerification$ = this.getUser(authUser.uid).pipe(
            map(user => (user.roles?.admin || user.roles?.manager || user.roles?.segmenter)
              ? 'onboarded'
              : 'unauthorised'
            ),
          );

          if (!authUser.emailVerified) {
            return concat(
              of<OnboardingStage>('email-not-verified'),
              interval(5000).pipe(
                mergeMap(async () => {
                  console.log('refreshing user to check email verification...');
                  const user = await this.auth.currentUser;
                  await user.reload();
                  return user.emailVerified;
                }),
                takeWhile(verified => !verified),
                ignoreElements(),
              ),
              statusAfterVerification$,
            );
          }

          return statusAfterVerification$;
        }
      }),
      retry({
        // eslint-able-next-line @typescript-eslint/no-explicit-any
        delay: (error: any, retryCount: number) => {
          if(retryCount >= 3) throw error
          const delay = retryCount * 2
          console.warn(`Retrying in ${delay} seconds after ${retryCount} attempts, error:`, error)
          return timer(delay * 1000)
        },
        resetOnSuccess: true,
      }),
    );

  private removeTokenFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('token');
    const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
    window.history.replaceState({}, document.title, newUrl);
    this.router.navigate([newUrl]);
  }

  private usersWithRole = (role: string): Observable<UserWithId[]> =>
    collectionData(
      query<UserWithId, DocumentData>(
        // eslint-able-next-line @typescript-eslint/no-explicit-any
        collection(this.db, 'users') as any,
        where(`roles.${role}`, '==', true),
      ),
      {idField: 'id'},
    ) as Observable<UserWithId[]>

  private userAndThenUserWithPhoto = (user: UserWithId): Observable<UserWithId | UserWithPhoto> =>
    concat(
      of(user), // emit user straight away
      user.photoPath // then optionally admit the user with a url
        ? from(getDownloadURL(ref(this.storage, user.photoPath))).pipe(
          map(photoURL => ({...user, photoURL} as UserWithPhoto))
        )
        : EMPTY
    )

  private _admins$     = this.usersWithRole('admin')
  private _managers$   = this.usersWithRole('manager')
  private _segmenters$ = this.usersWithRole('segmenter')

  // duplicates/triplicates fetching of users with multiple roles, but empirically cheaper than getting all users
  users$: Observable<UserWithId[]> = combineLatest([this._admins$, this._managers$, this._segmenters$]).pipe(
    map(userss => uniqBy(userss.flat(), 'id') as UserWithId[])
  )
  usersById$: Observable<Record<string, UserWithId>> = this.users$.pipe(
    map(users => Object.fromEntries(users.map(u => [u.id, u]))),
    shareReplay({bufferSize: 1, refCount: true}),
  )

  usersWithPhoto$ = this.users$.pipe(
    mergeAll(),
    groupBy(u => u.id),
    mergeMap(u$ => u$.pipe(
      switchMap(this.userAndThenUserWithPhoto)
    )),
    // eslint-able-next-line @typescript-eslint/consistent-indexed-object-style
    scan((acc, user) => ({...acc, [user.id]: user}), {} as {[id: string]: UserWithId | UserWithPhoto}),
    throttleTime(500, undefined, {leading: true, trailing: true}),
    shareReplay({bufferSize: 1, refCount: false}),
  )

  segmenters$ = this._segmenters$.pipe(
    map(users => users.sort(userNamesAndEmailsDropdownListSort))
  )

  mySegmenters$: Observable<UserWithId[]> = this.myRoles$.pipe(
    switchMap(roles => {
      if(roles?.admin) return this.segmenters$
      if(roles?.manager) return this.squads.mySquad$.pipe(
        switchMap(squad =>
          combineLatestOrEmptyArray(squad?.segmenters.map(this.getUser))
        ),
      )
      return of([])
    }),
    shareReplay({bufferSize: 1, refCount: true}),
  )

  getUser = (id: string): Observable<UserWithId> =>
    docData<UserWithId>(
      // eslint-able-next-line @typescript-eslint/no-explicit-any
      doc(this.db, `users/${id}`) as any,
      {idField: 'id'},
    )
}
